import React from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import { Icons } from '@ohif/ui-next';

const persistedActiveSubmenuItem = { current: null };
const persistedActiveLabel = { current: 'Window Level' };

const ContextMenu = ({ items, groups, layout, ...props }) => {
  const [activeSubmenu, setActiveSubmenu] = React.useState(null);
  const [submenuItems, setSubmenuItems] = React.useState([]);
  const [hoveredItem, setHoveredItem] = React.useState(null);
  const [activeLabel, setActiveLabel] = React.useState(persistedActiveLabel.current);
  const [centerLabel, setCenterLabel] = React.useState(persistedActiveLabel.current);
  const [activeSubmenuItem, setActiveSubmenuItem] = React.useState(persistedActiveSubmenuItem.current);
  const [initialized, setInitialized] = React.useState(false);


  React.useEffect(() => {
    if (layout !== 'circular') {
      return;
    }

    if (props?.menuId === 'measure') {
      setActiveSubmenu('measure');
    } else if (props?.menuId === 'windowPreset') {
      setActiveSubmenu('windowPreset');
    } else {
      setActiveSubmenu(null);
    }

    setSubmenuItems([]);
  }, [props?.menuId, layout]);

  if (!items && !groups) {
    return null;
  }

  const renderItems = groups || [{ items }];
  let allItems = renderItems.flatMap(group => group.items);


  if (layout === 'circular' && props?.eventData?.element) {
    try {
      const enabledElement = window.cornerstone?.getEnabledElement?.(props.eventData.element);
      const viewport = enabledElement?.viewport;
      const metadata = viewport?.getImageData?.()?.metadata;
      const modality = metadata?.Modality;

      if (modality && modality !== 'CT') {
        allItems = allItems.filter(item => item.label !== 'Window Preset');
      }
    } catch (e) {

    }
  }


  if (layout === 'circular') {
    const radius = 100;
    const outerRadius = 150;
    const centerX = 160;
    const centerY = 160;

    const handleItemClick = (item, skipLabelUpdate = false) => {
      if (!skipLabelUpdate) {
        persistedActiveLabel.current = item.label;
        setActiveLabel(item.label);
        setCenterLabel(item.label);
      }

      if (item.label === 'Measure') {
        const measureItems = [
          { label: 'Length', iconLeft: 'tool-length', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'Length', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Bidirectional', iconLeft: 'tool-bidirectional', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'Bidirectional', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Annotation', iconLeft: 'tool-annotate', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'ArrowAnnotate', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Ellipse', iconLeft: 'tool-ellipse', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'EllipticalROI', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Rectangle', iconLeft: 'tool-rectangle', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'RectangleROI', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Circle', iconLeft: 'tool-circle', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'CircleROI', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Freehand', iconLeft: 'icon-tool-freehand-roi', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'Freehand', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Spline', iconLeft: 'icon-tool-spline-roi', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'SplineROI', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } },
          { label: 'Livewire', iconLeft: 'icon-tool-livewire', commands: { commandName: 'setToolActiveToolbar', commandOptions: { toolName: 'Livewire', toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] } } }
        ];
        setActiveSubmenu('measure');
        setSubmenuItems(measureItems.map(subItem => ({
          ...subItem,
          action: () => {
            if (subItem.commands) {
              props.onDefault(subItem, subItem, props);
            }
            persistedActiveSubmenuItem.current = subItem.label;
            persistedActiveLabel.current = subItem.label;
            setActiveSubmenuItem(subItem.label);
            setActiveLabel(subItem.label);
            setCenterLabel(subItem.label);
            setActiveSubmenu(null);
            setSubmenuItems([]);
            props.onClose?.();
          }
        })));
      } else if (item.label === 'Window Preset') {
        const presetItems = [
          { label: 'Display Color bar', subtitle: '400 / 40', iconLeft: 'tool-window-level', commands: { commandName: 'toggleViewportColorbar', commandOptions: {} } },
          { label: 'Reset', subtitle: 'Auto', iconLeft: 'tool-reset', commands: { commandName: 'resetViewport', commandOptions: {} } },
          { label: 'Soft tissue', subtitle: '400 / 40', iconLeft: 'icon-soft-tissue', commands: { commandName: 'setWindowLevel', commandOptions: { windowWidth: '400', windowCenter: '40' } } },
          { label: 'Lung', subtitle: '1500 / -600', iconLeft: 'icon-lung', commands: { commandName: 'setWindowLevel', commandOptions: { windowWidth: '1500', windowCenter: '-600' } } },
          { label: 'Liver', subtitle: '150 / 90', iconLeft: 'icon-liver', commands: { commandName: 'setWindowLevel', commandOptions: { windowWidth: '150', windowCenter: '90' } } },
          { label: 'Bone', subtitle: '2500 / 480', iconLeft: 'icon-bone', commands: { commandName: 'setWindowLevel', commandOptions: { windowWidth: '2500', windowCenter: '480' } } },
          { label: 'Brain', subtitle: '80 / 40', iconLeft: 'icon-brain', commands: { commandName: 'setWindowLevel', commandOptions: { windowWidth: '80', windowCenter: '40' } } }
        ];
        setActiveSubmenu('windowPreset');
        setSubmenuItems(presetItems.map(subItem => ({
          ...subItem,
          action: () => {
            if (subItem.commands) {
              props.onDefault(subItem, subItem, props);
            }
            persistedActiveSubmenuItem.current = subItem.label;
            persistedActiveLabel.current = subItem.label;
            setActiveSubmenuItem(subItem.label);
            setActiveLabel(subItem.label);
            setCenterLabel(subItem.label);
            setActiveSubmenu(null);
            setSubmenuItems([]);
            props.onClose?.();
          }
        })));
      } else if (item.actionType === 'ShowSubMenu') {
        const submenu = props.menus?.find(menu => menu.id === item.subMenu);
        if (submenu && submenu.items) {
          setActiveSubmenu(item.subMenu);
          setSubmenuItems(submenu.items.map(subItem => ({
            ...subItem,
            action: () => {
              props.onDefault(subItem, subItem, props);
              persistedActiveSubmenuItem.current = subItem.label;
              persistedActiveLabel.current = subItem.label;
              setActiveSubmenuItem(subItem.label);
              setActiveLabel(subItem.label);
              setCenterLabel(subItem.label);
              setActiveSubmenu(null);
              setSubmenuItems([]);
              props.onClose?.();
            }
          })));
        }
      } else {
        persistedActiveSubmenuItem.current = null;
        persistedActiveLabel.current = item.label;
        setActiveSubmenuItem(null);
        setActiveLabel(item.label);
        setCenterLabel(item.label);
        item.action(item, props);
      }
    };


    React.useEffect(() => {
      if (initialized || !persistedActiveSubmenuItem.current) {
        return;
      }

      const measureItems = ['Length', 'Bidirectional', 'Annotation', 'Ellipse', 'Rectangle', 'Circle', 'Freehand', 'Spline', 'Livewire'];
      const presetItems = ['Soft tissue', 'Lung', 'Liver', 'Bone', 'Brain'];

      if (measureItems.includes(persistedActiveSubmenuItem.current)) {
        const item = allItems.find(i => i.label === 'Measure');
        if (item) handleItemClick(item, true);
      } else if (presetItems.includes(persistedActiveSubmenuItem.current)) {
        const item = allItems.find(i => i.label === 'Window Preset');
        if (item) handleItemClick(item, true);
      }
      setInitialized(true);
    }, [initialized]);

    return (
      <div
        data-cy="context-menu"
        className="relative w-[320px] h-[320px] z-50"
        onContextMenu={e => e.preventDefault()}
      >
        <svg width="320" height="320" className="absolute inset-0">
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="5" stdDeviation="3" floodColor="#0003" floodOpacity="1"/>
              <feDropShadow dx="0" dy="11" stdDeviation="8" floodColor="#00000012" floodOpacity="1"/>
              <feDropShadow dx="0" dy="4" stdDeviation="10" floodColor="#0000000f" floodOpacity="1"/>
            </filter>
            <filter id="glassGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="0" dy="2" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.5"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <linearGradient id="glassGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.1)" stopOpacity="1" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" stopOpacity="1" />
            </linearGradient>
          </defs>
          {allItems.map((item, index) => {
            const segmentAngle = 360 / allItems.length;
            const startAngle = index * segmentAngle - 90;
            const endAngle = startAngle + segmentAngle;
            const midAngle = startAngle + segmentAngle / 2;

            const outerRadius = 100;
            const innerRadius = 40;

            const startAngleRad = (startAngle * Math.PI) / 180;
            const endAngleRad = (endAngle * Math.PI) / 180;

            const x1 = centerX + innerRadius * Math.cos(startAngleRad);
            const y1 = centerY + innerRadius * Math.sin(startAngleRad);
            const x2 = centerX + outerRadius * Math.cos(startAngleRad);
            const y2 = centerY + outerRadius * Math.sin(startAngleRad);

            const x3 = centerX + outerRadius * Math.cos(endAngleRad);
            const y3 = centerY + outerRadius * Math.sin(endAngleRad);
            const x4 = centerX + innerRadius * Math.cos(endAngleRad);
            const y4 = centerY + innerRadius * Math.sin(endAngleRad);

            const largeArcFlag = segmentAngle > 180 ? 1 : 0;

            const pathData = [
              `M ${x1} ${y1}`,
              `L ${x2} ${y2}`,
              `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x3} ${y3}`,
              `L ${x4} ${y4}`,
              `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}`,
              'Z'
            ].join(' ');

            const textRadius = (innerRadius + outerRadius) / 2;
            const textAngleRad = (midAngle * Math.PI) / 180;
            const textX = centerX + textRadius * Math.cos(textAngleRad);
            const textY = centerY + textRadius * Math.sin(textAngleRad);

            const arrowRadius = outerRadius - 8;
            const arrowX = centerX + arrowRadius * Math.cos(textAngleRad);
            const arrowY = centerY + arrowRadius * Math.sin(textAngleRad);
            const hasSubmenu = item.label === 'Measure' || item.label === 'Window Preset' || item.actionType === 'ShowSubMenu';

            return (
              <g key={index}>
                <path
                  d={pathData}
                  fill={(item.label === 'Measure' && activeSubmenu === 'measure') || (item.label === 'Window Preset' && activeSubmenu === 'windowPreset') || (item.label === activeLabel && !activeSubmenuItem) ? "#e18131" : "#18181b "}
                  stroke={(item.label === 'Measure' && activeSubmenu === 'measure') || (item.label === 'Window Preset' && activeSubmenu === 'windowPreset') || (item.label === activeLabel && !activeSubmenuItem) ? "#af5e1cff" : "#18181b "}
                  strokeWidth="1"
                  filter={(item.label === 'Measure' && activeSubmenu === 'measure') || (item.label === 'Window Preset' && activeSubmenu === 'windowPreset') || (item.label === activeLabel && !activeSubmenuItem) ? "url(#glassGlow)" : "none"}
                  className="hover:fill-[#2d5a6e] cursor-pointer transition-colors"
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => {
                    setHoveredItem(index);
                    setCenterLabel(item.label);
                  }}
                  onMouseLeave={() => {
                    setHoveredItem(null);
                    setCenterLabel(activeLabel);
                  }}
                />
                {item.iconLeft && (
                  <foreignObject
                    x={textX - 12}
                    y={textY - 12}
                    width="24"
                    height="24"
                    className="pointer-events-none"
                    style={{ color: 'white', overflow: 'visible' }}
                  >
                    <Icons.ByName
                      name={item.iconLeft}
                      className="text-white w-6 h-6"
                      style={{ color: 'white' }}
                    />
                  </foreignObject>
                )}
                {hasSubmenu && (
                  <polygon
                    points={`${arrowX - 3 * Math.sin(textAngleRad)},${arrowY + 3 * Math.cos(textAngleRad)} ${arrowX + 3 * Math.sin(textAngleRad)},${arrowY - 3 * Math.cos(textAngleRad)} ${arrowX + 6 * Math.cos(textAngleRad)},${arrowY + 6 * Math.sin(textAngleRad)}`}
                    fill="white"
                    className="pointer-events-none"
                  />
                )}
                <title>{item.label}</title>
              </g>
            );
          })}

          <circle
            cx={centerX}
            cy={centerY}
            r="100"
            fill="none"
            stroke="#fb9b35ff"
            strokeWidth="2"
            filter="url(#glow)"
            className="pointer-events-none"
          />
          <circle
            cx={centerX}
            cy={centerY}
            r="40"
            fill="none"
            stroke="#fb9b35ff"
            strokeWidth="2"
            filter="url(#glow)"
            className="pointer-events-none"
          />
          <circle
            cx={centerX}
            cy={centerY}
            r="40"
            fill="#1f1e18"
            stroke="none"
          />
          <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontSize="11"
            fontWeight="500"
            className="pointer-events-none select-none"
          >
            {centerLabel.split(' ').length > 1 ? (
              <>
                <tspan x={centerX} dy="-6">{centerLabel.split(' ')[0]}</tspan>
                <tspan x={centerX} dy="14">{centerLabel.split(' ').slice(1).join(' ')}</tspan>
              </>
            ) : (
              centerLabel
            )}
          </text>
        </svg>

        {/* Outer ring for submenu items */}
        {activeSubmenu && submenuItems.length > 0 && (
          <svg
            width="320"
            height="320"
            className="absolute inset-0"
            key={`${activeSubmenu}-${submenuItems.length}`}
            style={{ pointerEvents: 'none' }}
          >
            {submenuItems.map((item, index) => {
              const segmentAngle = 360 / submenuItems.length;
              const startAngle = index * segmentAngle - 90;
              const endAngle = startAngle + segmentAngle;
              const midAngle = startAngle + segmentAngle / 2;

              const outerRadius = 150;
              const innerRadius = 100;

              const startAngleRad = (startAngle * Math.PI) / 180;
              const endAngleRad = (endAngle * Math.PI) / 180;

              const x1 = centerX + innerRadius * Math.cos(startAngleRad);
              const y1 = centerY + innerRadius * Math.sin(startAngleRad);
              const x2 = centerX + outerRadius * Math.cos(startAngleRad);
              const y2 = centerY + outerRadius * Math.sin(startAngleRad);

              const x3 = centerX + outerRadius * Math.cos(endAngleRad);
              const y3 = centerY + outerRadius * Math.sin(endAngleRad);
              const x4 = centerX + innerRadius * Math.cos(endAngleRad);
              const y4 = centerY + innerRadius * Math.sin(endAngleRad);

              const largeArcFlag = segmentAngle > 180 ? 1 : 0;

              const pathData = [
                `M ${x1} ${y1}`,
                `L ${x2} ${y2}`,
                `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x3} ${y3}`,
                `L ${x4} ${y4}`,
                `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}`,
                'Z'
              ].join(' ');

              const textRadius = (innerRadius + outerRadius) / 2;
              const textAngleRad = (midAngle * Math.PI) / 180;
              const textX = centerX + textRadius * Math.cos(textAngleRad);
              const textY = centerY + textRadius * Math.sin(textAngleRad);


              const sepX1 = centerX + innerRadius * Math.cos(startAngleRad);
              const sepY1 = centerY + innerRadius * Math.sin(startAngleRad);
              const sepX2 = centerX + outerRadius * Math.cos(startAngleRad);
              const sepY2 = centerY + outerRadius * Math.sin(startAngleRad);

              return (
                <g key={index} style={{ pointerEvents: 'auto' }}>
                  <path
                    d={pathData}
                    fill={activeSubmenuItem === item.label ? "#1d1c19ff" : "#1f1e18"}
                    stroke="none"
                    filter="url(#glow)"
                    className="hover:fill-[#3d5a6f] cursor-pointer transition-colors"
                    onClick={() => item.action()}
                    onMouseEnter={() => setCenterLabel(item.label)}
                    onMouseLeave={() => setCenterLabel(activeLabel)}
                  />
                  <line
                    x1={sepX1}
                    y1={sepY1}
                    x2={sepX2}
                    y2={sepY2}
                    stroke="hsla(0, 0%, 100%, 0.3)"
                    strokeWidth="1"
                    className="pointer-events-none"
                  />
                  {item.iconLeft && (
                    <foreignObject
                      x={textX - 10}
                      y={textY - 10}
                      width="20"
                      height="20"
                      className="pointer-events-none"
                      style={{ color: 'white', overflow: 'visible' }}
                    >
                      <Icons.ByName
                        name={item.iconLeft}
                        className="text-white w-5 h-5"
                        style={{ color: 'white' }}
                      />
                    </foreignObject>
                  )}
                  <title>{item.label}</title>
                </g>
              );
            })}
          </svg>
        )}
      </div>
    );
  }


  return (
    <div
      data-cy="context-menu"
      className="bg-secondary-dark relative z-50 block w-48 rounded"
      onContextMenu={e => e.preventDefault()}
    >
      {renderItems.map((group, groupIndex) => (
        <div key={groupIndex}>
          {group.items.map((item, index) => (
            <div
              key={index}
              data-cy="context-menu-item"
              onClick={() => item.action(item, props)}
              className="hover:bg-primary-dark border-primary-dark flex cursor-pointer items-center border-b px-4 py-3 transition duration-300 last:border-b-0"
            >
              {item.iconLeft && (
                <Icons.ByName
                  name={item.iconLeft}
                  className="mr-3 inline text-white"
                />
              )}
              <Typography component="span" className="text-white">{item.label}</Typography>
              {item.iconRight && (
                <Icons.ByName
                  name={item.iconRight}
                  className="inline text-white"
                />
              )}
            </div>
          ))}
          {groupIndex < renderItems.length - 1 && (
            <div className="border-primary-dark border-b mx-2" />
          )}
        </div>
      ))}
    </div>
  );
};

ContextMenu.propTypes = {
  defaultPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
    })
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          action: PropTypes.func.isRequired,
        })
      ).isRequired,
    })
  ),
};

export default ContextMenu;
